<template>
    <section name="course section" class="py-lg px-sm lg:px-xl gap-xl flex flex-col">
        <div
            v-for="content in section.contents"
            :id="'content_' + section.contents.findIndex((c) => c === content)"
            :key="content.content_id"
            name="contentRefs"
        >
            <h1 class="mb-sm">{{ content.title }}</h1>

            <!-- Paragraphs are rich HTML authored by atNorth admins in the LMS CMS, not end-user input. -->
            <!-- eslint-disable vue/no-v-html -->
            <div
                v-for="paragraph in content.paragraphs"
                :key="paragraph.content_paragraph_id"
                v-html="paragraph.paragraph"
            ></div>
            <!-- eslint-enable vue/no-v-html -->
            <img
                v-for="image in content.images"
                :key="image.content_image_id"
                :src="`https://files.atnorth.com/customer-portal-images/${image.file_id}`"
            />
            <media-player
                v-for="video in content.videos"
                :key="video.content_video_id"
                .src="`https://files.atnorth.com/customer-portal-videos/${video.file_id}`"
                type="video/mp4"
            >
                <media-provider></media-provider>
                <media-video-layout> </media-video-layout>
            </media-player>
        </div>

        <form
            v-for="quiz in section.quizes"
            :id="'quiz_' + section.quizes.findIndex((q) => q === quiz)"
            :key="quiz.quiz_id"
            name="contentRefs"
            class="gap-md flex flex-col"
            @submit.prevent
        >
            <div v-if="incorrect" class="gap-lg p-md flex flex-col rounded-md bg-(--state-danger-bg)">
                <h1>Quiz Results</h1>
                <div class="gap-sm flex flex-col">
                    <div class="gap-xs flex">
                        <p>Status</p>

                        <div class="px-2xs gap-xs flex items-center">
                            <div class="h-2 w-2 rounded-full bg-(--state-danger-fg)"></div>
                            <span class="font-semibold text-(--state-danger-fg)">NOT PASSED</span>
                        </div>
                    </div>
                    <p>
                        Oops! It seems like there was a mistake in your answers. To unlock the next lesson, you’ll need
                        to try the quiz again and complete it correctly. Don’t worry—this is a great chance to review
                        and improve! Click ‘Retry’ to start over and show what you’ve learned!”
                    </p>
                </div>
            </div>
            <div v-for="question in quiz.questions" :key="question.question_id" class="gap-sm flex flex-col">
                <div class="gap-2xs flex flex-col">
                    <h2>{{ question.title }}</h2>
                    <p v-if="!incorrect">Choose 1 answer</p>
                    <p v-else>Your answer</p>
                </div>
                <div v-if="!incorrect && !completedQuiz" class="gap-sm flex flex-col">
                    <div
                        v-for="option in question.options"
                        :key="option.question_option_id"
                        class="p-md gap-sm flex items-center justify-between rounded-md border transition-colors duration-300"
                        :class="{
                            'border-(--border-focus)': selectedOptions?.includes(option),
                            'cursor-pointer': !completedQuiz,
                        }"
                        @click="toggleOption(option)"
                    >
                        <label :for="`option_${option.question_option_id}`">{{ option.question }}</label>
                        <input
                            :id="`question_${question.question_id}option_${option.question_option_id}`"
                            type="radio"
                            class="shrink-0"
                            :checked="selectedOptions?.includes(option)"
                        />
                    </div>
                </div>
                <div v-else>
                    <div
                        class="gap-sm p-md flex flex-col rounded-md"
                        :class="
                            selectedOptions.find((o) => o.question_id === question.question_id)?.correct
                                ? 'bg-(--state-success-bg)'
                                : 'bg-(--state-danger-bg)'
                        "
                    >
                        <span
                            v-if="selectedOptions.find((o) => o.question_id === question.question_id)?.correct"
                            class="text-(--state-success-fg)"
                            >Correct</span
                        >
                        <span v-else class="text-(--state-danger-fg)">Incorrect</span>
                        <p>
                            {{ selectedOptions.find((o) => o.question_id === question.question_id)?.question }}
                        </p>
                    </div>
                </div>
            </div>
        </form>
    </section>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, type PropType } from 'vue';
import 'vidstack/bundle';
import type { QuestionOption, Section } from '@/stores/store-interfaces';

const selectedOptions = ref<QuestionOption[]>([]);
const emit = defineEmits(['optionsSelected']);

const props = defineProps({
    section: {
        type: Object as PropType<Section>,
        required: true,
    },
    incorrect: {
        type: Boolean,
        required: false,
    },
    completedQuiz: {
        type: Boolean,
        required: false,
    },
});

onMounted(() => {
    if (props.completedQuiz) {
        setupCorrectAnswers();
    }
});

function setupCorrectAnswers() {
    selectedOptions.value = props.section.quizes[0]?.questions.map((q) => {
        return q.options.find((o) => o.correct) as QuestionOption;
    });
}

function toggleOption(option: QuestionOption) {
    if (props.completedQuiz) return;
    const presentOption = selectedOptions.value?.find((o) => o.question_id === option.question_id);
    if (presentOption) {
        selectedOptions.value = selectedOptions.value.filter((o) => o.question_id !== option.question_id);
        selectedOptions.value.push(option);
    } else {
        selectedOptions.value.push(option);
    }
}

watch(
    () => selectedOptions.value,
    () => {
        if (
            selectedOptions.value?.length === props.section.quizes[0]?.questions?.length &&
            selectedOptions.value?.length > 0
        ) {
            emit('optionsSelected', selectedOptions.value);
        }
    },
    { deep: true },
);

watch(
    () => props.completedQuiz,
    (newVal) => {
        if (newVal) {
            setupCorrectAnswers();
        }
    },
);
</script>

<style scoped>
@reference '@/assets/styles/default.css';
/* Apply blue color to markers only (list-decimal or list-disc) */
:deep(ol) {
    @apply ml-5 list-decimal; /* Ensure proper decimal list styles */
}

:deep(ul) {
    @apply ml-5 list-disc; /* Ensure proper disc list styles */
}

:deep(li) {
    @apply py-xs; /* Ensure proper list alignment */
}

:deep(ol > li::marker) {
    @apply text-blue-500; /* Apply blue color to decimal markers */
}

:deep(ul > li::marker) {
    @apply text-blue-500; /* Apply blue color to disc markers */
}
</style>
